// Basic company constants - defined here to avoid circular dependency
const PHONE_NUMBER = '+1-800-655-6303'
const COMPANY_NAME = 'Freedom Debt Relief, LLC'
const WEBSITE_CONTACT_NUMBER = '+18009100065'

// Centralized rating data source - single source of truth
const DEFAULT_RATING_DATA = {
  ratingValue: '4.6',
  ratingCount: '44786',
  reviewCount: '44786',
}

// Function to get consistent rating data with optional overrides
const getRatingData = (overrides = {}) => {
  // Filter out undefined/null values from overrides to prevent them from being used
  const validOverrides = {}
  if (overrides) {
    Object.keys(overrides).forEach((key) => {
      if (overrides[key] !== undefined && overrides[key] !== null) {
        validOverrides[key] = overrides[key]
      }
    })
  }

  return {
    ...DEFAULT_RATING_DATA,
    ...validOverrides,
  }
}

// Function to create consistent aggregateRating schema object
const createAggregateRatingSchema = (overrides = {}) => {
  const ratingData = getRatingData(overrides)

  // Normalize values to strings for schema.org compliance
  return {
    '@type': 'AggregateRating',
    ratingValue: String(ratingData.ratingValue),
    ratingCount: String(ratingData.ratingCount),
    reviewCount: String(ratingData.reviewCount),
  }
}

// ============================================================================
// SHARED SCHEMA COMPONENTS - Single source of truth for reusable data
// ============================================================================

/**
 * Shared postal address - used across all organization schemas
 * Maintains consistency for company address information
 */
const SHARED_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '1875 S Grant St Ste 300',
  addressLocality: 'San Mateo',
  addressRegion: 'CA',
  postalCode: '94402',
  addressCountry: 'US',
}

/**
 * Shared postal address with Country object format
 * Used in schemas that require the expanded Country format
 */
const SHARED_ADDRESS_WITH_COUNTRY_OBJECT = {
  '@type': 'PostalAddress',
  streetAddress: '1875 S Grant St Ste 300',
  addressLocality: 'San Mateo',
  addressRegion: 'CA',
  postalCode: '94402',
  addressCountry: {
    '@type': 'Country',
    name: 'US',
  },
}

/**
 * Base sameAs links - core company profiles
 * These are included in all organization schemas
 */
const BASE_SAME_AS = [
  'https://www.linkedin.com/company/freedom-debt-relief/',
  'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9',
  'https://www.dnb.com/business-directory/company-profiles.freedom_debt_relief_llc.166a0dadaba157f009a4744c0bd8c1d7.html',
  'https://www.wikidata.org/wiki/Q133894770',
]

/**
 * Extended sameAs links for homepage - includes social media
 * Used specifically for homepage organization schema
 */
const HOMEPAGE_SAME_AS = [
  'https://twitter.com/freedomdebt',
  'https://www.facebook.com/freedomdebtrelief/',
  'https://www.linkedin.com/company/freedom-debt-relief/',
  'https://www.youtube.com/freedomdebtrelief',
  'https://www.instagram.com/freedomdebtrelief/',
  'https://www.google.com/search?kgmid=/g/11stymh6d3',
  'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9',
  'https://www.dnb.com/business-directory/company-profiles.freedom_debt_relief_llc.166a0dadaba157f009a4744c0bd8c1d7.html',
  'https://www.wikidata.org/wiki/Q133894770',
]

/**
 * Business office contact point
 * Used for general organization schemas
 */
const BUSINESS_OFFICE_CONTACT = {
  '@type': 'ContactPoint',
  telephone: '+18002301553',
  contactType: 'business office',
}

/**
 * Customer service contact point
 * Used for local business and customer-facing schemas
 */
const CUSTOMER_SERVICE_CONTACT = {
  '@type': 'ContactPoint',
  telephone: PHONE_NUMBER,
  contactType: 'customer service',
}

// ============================================================================
// CENTRALIZED ORGANIZATION SCHEMAS
// ============================================================================

/**
 * Centralized Schema.org constants for consistent structured data across all pages
 *
 * This file provides 5 standardized schema constants that should be used
 * throughout the application to ensure consistency and prevent duplicate
 * organization schemas on the same page.
 *
 * Usage:
 * - Import the specific schema constant you need
 * - Use as-is or merge with page-specific overrides
 * - Never create duplicate Organization schemas on the same page
 */

// Base organization data - shared across all organization schemas
export const BASE_ORGANIZATION_DATA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.freedomdebtrelief.com/#organization',
  logo: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
  legalName: COMPANY_NAME,
  name: COMPANY_NAME,
  url: 'https://www.freedomdebtrelief.com',
  description: `${COMPANY_NAME} is the largest debt settlement company in the United States, specializing in helping consumers resolve unsecured debt, particularly credit card debt.`,
  sameAs: BASE_SAME_AS,
  address: SHARED_ADDRESS,
  contactPoint: [BUSINESS_OFFICE_CONTACT],
}

// Base local business data - extends organization for location-specific schemas
const BASE_LOCAL_BUSINESS_DATA = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Freedom Debt Relief',
  url: 'https://www.freedomdebtrelief.com',
  logo: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
  image: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
  hasMap: 'https://www.google.com/maps?cid=1138888979161863326',
  description:
    'Freedom Debt Relief helps clients lower their monthly debt payments without bankruptcy or loans.',
  address: SHARED_ADDRESS,
  contactPoint: [CUSTOMER_SERVICE_CONTACT],
  potentialAction: {
    '@type': 'WriteAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://g.page/r/CZ7UjBF-Jc4PEAI/review',
      inLanguage: 'en',
      actionPlatform: [
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/MobileWebPlatform',
      ],
    },
  },
  parentOrganization: {
    '@type': 'Organization',
    name: 'Freedom Debt Relief, LLC',
    legalName: 'Freedom Debt Relief, LLC',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
    },
    sameAs: [
      'https://www.linkedin.com/company/freedom-debt-relief/',
      'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9',
    ],
  },
}

/**
 * 1. STANDARD_ORGANIZATION_SCHEMA
 * Main organization schema for general pages and homepage
 * Use this for most pages that need organization structured data
 */
export const STANDARD_ORGANIZATION_SCHEMA = {
  ...BASE_ORGANIZATION_DATA,
}

/**
 * 3. LOCAL_BUSINESS_SCHEMA
 * Local business schema for contact and location pages
 * Use this for pages that emphasize physical location and local services
 */
export const LOCAL_BUSINESS_SCHEMA = {
  ...BASE_LOCAL_BUSINESS_DATA,
}

/**
 * 4. ABOUT_PAGE_ORGANIZATION_SCHEMA
 * Organization schema specifically for About page publisher data
 * Used as publisher reference in AboutPage schema
 */
export const ABOUT_PAGE_ORGANIZATION_SCHEMA = {
  '@type': 'Organization',
  '@id': 'https://www.freedomdebtrelief.com/#organization',
  logo: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
  legalName: COMPANY_NAME,
  name: COMPANY_NAME,
  sameAs: BASE_SAME_AS,
  address: SHARED_ADDRESS_WITH_COUNTRY_OBJECT,
  contactPoint: [BUSINESS_OFFICE_CONTACT],
}

/**
 * Utility function to create organization schema with custom overrides
 * Use this when you need to customize any of the base schemas
 *
 * @param {Object} baseSchema - One of the 5 base schemas above
 * @param {Object} overrides - Custom properties to override
 * @param {Object} trustMarkerData - Optional trust marker data for ratings
 * @returns {Object} Customized organization schema
 */
export const createCustomOrganizationSchema = (
  baseSchema,
  overrides = {},
  trustMarkerData = null
) => {
  const schema = { ...baseSchema, ...overrides }

  // Apply trust marker data if provided
  if (trustMarkerData) {
    schema.aggregateRating = createAggregateRatingSchema({
      ratingValue: trustMarkerData.ratingValue,
      ratingCount: trustMarkerData.ratingCount,
      reviewCount: trustMarkerData.reviewCount,
    })
  }

  return schema
}

/**
 * Website schema for homepage (static version)
 * @deprecated Use createWebsiteSchema(trustMarkerData) for dynamic ratings
 * Represents the entire website entity
 */

// Export rating functions and basic constants for use in other files
export {
  DEFAULT_RATING_DATA,
  getRatingData,
  createAggregateRatingSchema,
  PHONE_NUMBER,
  COMPANY_NAME,
}
